/**
 * Manage Event Members Dialog
 *
 * Reason: Allows admins to assign users (viewers and managers) to events
 * for access control (many-to-many relationship)
 */

import { FC, useState, useContext } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  TextField,
  MenuItem,
  Typography,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useQueryClient } from 'react-query';
import {
  useListEventMembers,
  useAssignUserToEvent,
  useRemoveUserFromEvent,
  LIST_EVENT_MEMBERS_CACHE_KEY,
  Event,
} from '@/adapters/events-v2';
import { useListOrgUsers } from '@/adapters/users-v2';
import { UserContext } from '@/contexts/UserContext';

interface ManageEventMembersDialogProps {
  open: boolean;
  event: Event | null;
  onClose(): void;
}

/**
 * Dialog for managing event member assignments
 *
 * @reason Provides UI for admins to assign and remove users from events
 */
const ManageEventMembersDialog: FC<ManageEventMembersDialogProps> = ({ open, event, onClose }) => {
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<'manager' | 'viewer'>('viewer');
  const [error, setError] = useState<string | null>(null);

  const { userState } = useContext(UserContext);
  const queryClient = useQueryClient();

  // Fetch event members
  const { data: eventMembers = [], isLoading: loadingMembers } = useListEventMembers(event?.id);

  // Fetch all org users to select from
  const { data: orgUsers = [], isLoading: loadingUsers } = useListOrgUsers(userState?.orgId);

  // Mutations
  const assignMutation = useAssignUserToEvent(event?.id || '');
  const removeMutation = useRemoveUserFromEvent(event?.id || '');

  /**
   * Filters users to show only managers (role=manager) and viewers
   * who are not already assigned to this event
   *
   * @reason Only managers and viewers can be assigned to events
   */
  const availableUsers = orgUsers.filter((user) => {
    // Only show managers (role=manager) and viewers
    if (user.role !== 'manager' && user.role !== 'viewer') return false;

    // Exclude users already assigned to this event (check by email)
    const isAssigned = eventMembers.some((member) => member.email === user.email);
    return !isAssigned;
  });

  /**
   * Handles assigning a user to the event
   *
   * @reason Creates many-to-many relationship between user and event
   */
  const handleAssign = async (): Promise<void> => {
    setError(null);

    if (!selectedEmail) {
      setError('Please select a user');
      return;
    }

    if (!event) return;

    try {
      await assignMutation.mutateAsync({
        email: selectedEmail,
        role: selectedRole,
        isActive: true,
      });

      // Invalidate cache to refresh member list
      await queryClient.invalidateQueries([LIST_EVENT_MEMBERS_CACHE_KEY, event.id]);

      // Reset selection
      setSelectedEmail('');
      setSelectedRole('viewer');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to assign user';
      setError(errorMessage);
    }
  };

  /**
   * Handles removing a user from the event
   *
   * @reason Removes many-to-many relationship
   */
  const handleRemove = async (email: string): Promise<void> => {
    if (!event) return;

    const member = eventMembers.find((m) => m.email === email);
    if (!member) return;

    const confirmRemove = window.confirm(
      `Are you sure you want to remove this user from the event?`,
    );

    if (!confirmRemove) return;

    setError(null);

    try {
      await removeMutation.mutateAsync(email);

      // Invalidate cache to refresh member list
      await queryClient.invalidateQueries([LIST_EVENT_MEMBERS_CACHE_KEY, event.id]);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove user';
      setError(errorMessage);
    }
  };

  /**
   * Handles dialog close
   *
   * @reason Cleans up state when dialog is closed
   */
  const handleClose = (): void => {
    setSelectedEmail('');
    setSelectedRole('viewer');
    setError(null);
    onClose();
  };

  /**
   * Gets user details by email
   *
   * @reason Helper to display user info in member list
   */
  const getUserDetails = (email: string): import('@/adapters/users-v2').OrgUser | undefined => {
    return orgUsers.find((u) => u.email === email);
  };

  const isLoading = assignMutation.isLoading || removeMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Event Members: {event?.name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Assign New User Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Assign User to Event
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              select
              label="Select User"
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
              disabled={isLoading || loadingUsers}
              sx={{ flex: 2 }}
              fullWidth
            >
              {availableUsers.length === 0 ? (
                <MenuItem disabled>No users available</MenuItem>
              ) : (
                availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.email}>
                    {user.email} - {user.role === 'manager' ? 'Manager' : 'Viewer'}
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              select
              label="Access Level"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as 'manager' | 'viewer')}
              disabled={isLoading}
              sx={{ flex: 1 }}
            >
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </TextField>

            <Button
              variant="contained"
              onClick={handleAssign}
              disabled={!selectedEmail || isLoading || loadingUsers}
              sx={{ mt: 1 }}
            >
              Assign
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Current Members Section */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Current Members ({eventMembers.length})
        </Typography>

        {loadingMembers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : eventMembers.length === 0 ? (
          <Typography color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
            No users assigned to this event yet
          </Typography>
        ) : (
          <List>
            {eventMembers.map((member) => {
              const userDetails = getUserDetails(member.email);
              return (
                <ListItem
                  key={member.id}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleRemove(member.email)}
                      disabled={isLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={member.email}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        <Chip
                          label={member.role === 'manager' ? 'Manager' : 'Viewer'}
                          size="small"
                          color={member.role === 'manager' ? 'primary' : 'default'}
                        />
                        {userDetails?.name && (
                          <Typography variant="caption" color="textSecondary">
                            {userDetails.name}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageEventMembersDialog;
